import { B2C, type ltFramework, sessionService } from '@life-time-inc/enterprise-framework-core';
import { EventConstant, ExperienceTypeConstant, MemberStateConstant } from '../constants';
import AlertNotificationStore from './alertNotificationStore';
import EventHub from '../eventHub';
import { analyticsService } from '../services/analyticsService';
import { urlService } from '../services/urlService';
import { featureFlagService } from '../services/featureFlagService';
import B2CStore from './b2cStore';
import ClubStore from './clubStore';
import ClubsStore from './clubsStore';
import NearestClubStore from './nearestClubStore';
import RecentClubStore from './recentClubStore';
import EmployeeProfileStore from './employeeProfileStore';
import NavigationStore from './navigationStore';
import ProfileStore from './profileStore';
import ProspectProfileStore from './prospectProfileStore';
import UrlStore from './urlStore';
import { getAuthZoneID } from '../helpers/tmZone';
import { updateMoxie } from '../utils/moxieSync';

export default class Framework {
	public static initialize(): Promise<void> {
		const promises = [];
		const experienceType: string
			= window.lt?.pageProperties?.experienceType
				? window.lt.pageProperties.experienceType.toLowerCase()
				: '';
		window.ef = {} as ltFramework.IFramework;
		window.ef.alertNotificationStore = new AlertNotificationStore();
		window.ef.clubsStore = new ClubsStore();
		window.ef.prospectProfileStore = new ProspectProfileStore();
		window.ef.recentClubStore = new RecentClubStore();
		window.ef.navigationStore = new NavigationStore();
		window.ef.nearestClubStore = new NearestClubStore();
		window.ef.urlStore = new UrlStore();
		window.ef.eventHub = EventHub;
		window.ef.sessionService = sessionService;
		window.ef.analyticsService = analyticsService;
		window.ef.urlService = urlService;
		window.ef.featureFlagService = featureFlagService;

		if (experienceType !== ExperienceTypeConstant.rare) {
			promises.push(window.ef.clubsStore.initialize());
		}
		promises.push(window.ef.urlStore.initialize());

		if (
			experienceType === ExperienceTypeConstant.member
			|| experienceType === ExperienceTypeConstant.sport
			|| experienceType === ExperienceTypeConstant.work
			|| experienceType === ExperienceTypeConstant.workProspect
			|| experienceType === ExperienceTypeConstant.living
		) {
			window.ef.clubStore = new ClubStore();

			const b2c = new B2C({
				service: 'enterprise-framework',
				// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
				version: import.meta.env.VITE_APP_VERSION
			});
			window.ef.b2cStore = new B2CStore();
			const disableProfileStore = featureFlagService.isEnabled('aemEfDisableProfileStore');
			if (!disableProfileStore) {
				window.ef.profileStore = new ProfileStore();
			}

			promises.push(
				b2c
					.handleB2CRedirect()
					.then(() => {
						return window.ef.b2cStore?.initialize();
					})
					.then(() => {
						if (disableProfileStore) {
							return;
						}
						return window.ef.profileStore.initialize();
					})
					.then(() => {
						let profileData: ltFramework.IProfile;
						if (disableProfileStore) {
							profileData = {
								memberState: MemberStateConstant.unknown,
								personalization: null
							} as ltFramework.IProfile;
						}
						else {
							profileData = window.ef.profileStore.getInitialData();
							if (window.pageConfig?.auth.forceJavascriptAuth === 'js-auth' && profileData.memberState !== 'AUTHENTICATED') {
								return window.ef.b2cStore?.login();
							}
						}
						return window.ef.recentClubStore.initialize(experienceType, profileData);
					})
			);

			promises.push(
				window.ef.clubStore.initialize().then(() => {
					const clubId = window.ef.clubStore.getInitialData().club.id;
					return window.ef.alertNotificationStore.initialize(clubId);
				})
			);
		}
		else if (experienceType === ExperienceTypeConstant.employee) {
			window.ef.employeeProfileStore = new EmployeeProfileStore();
			promises.push(window.ef.alertNotificationStore.initialize());
			if (!this.isLoginPage()) {
				promises.push(window.ef.employeeProfileStore.initialize());
			}
			promises.push(window.ef.navigationStore.initialize());
		}
		else if (experienceType === ExperienceTypeConstant.prospect) {
			window.ef.clubStore = new ClubStore();
			updateMoxie();
			promises.push(window.ef.alertNotificationStore.initialize());
			promises.push(window.ef.prospectProfileStore.initialize());
		}

		if (experienceType !== ExperienceTypeConstant.prospect
			&& window.lt?.pageProperties?.enableProspectProfileStore) {
			promises.push(window.ef.prospectProfileStore.initialize());
		}

		return Promise.all(promises).then(() => {
			// eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
			window.ef.eventHub.emit(EventConstant.efFrameworkLoaded);
		});
	}

	private static isLoginPage() {
		const loginResource = window.lt?.globalPageDrivers.global.login;
		const isLoginPage = window.location.pathname === loginResource;
		if (isLoginPage) {
			const zoneId = getAuthZoneID();
			sessionService.invalidateSession(zoneId);
		}
		return isLoginPage;
	}
}
